/**
 * ContainerDetailsPanel - Panel de detalles completo del contenedor con pestañas
 *
 * @version 2.0
 * @updated 2026-02-22
 * @module core/modules/waste/components/panels
 *
 * @description
 * Panel con 5 pestañas para gestión completa de contenedores de residuos:
 * 1. General - Identificación (código, tipo, capacidad)
 * 2. Estado - Estado actual y nivel de llenado
 * 3. Sensor - IoT y alertas
 * 4. Mant. - Mantenimiento e historial
 * 5. Ubic. - Ubicación y coordenadas
 *
 * @remarks
 * Implementa sistema de detección de cambios sin guardar para prevenir pérdida de datos.
 * Integra el sistema de codificación automática de objetos (core/coding):
 * - Contenedores nuevos: genera código CN_XXNNN_NNN al guardar
 * - Contenedores movidos a otra celda: registra cambio en historial y muestra aviso
 * - Sigue exactamente el patrón de TreeDetailsPanel
 */

import { useState, useEffect, useMemo } from 'react';
import { Copy, Check, AlertTriangle, Loader2 } from 'lucide-react';
import { useRightPanel, useUnsavedChanges } from '../../../../layouts/panels/RightPanel';
import { ConfirmDialog } from '../../../../components';
import { useObjectCode, ObjectCodeBadge, CodeChangeWarning } from '../../../../coding';
import { useProject } from '../../../../projects';
import { PointAssociationSection } from '../../../../spatial';
import { wasteApi, getWasteApiErrorMessage } from '../../services';
import { CONTAINER_TYPES, CONTAINER_STATUSES } from '../../constants';
import { FormLabel } from './FormLabel';
import type { WasteContainer, UpdateContainerDto } from '../../types';
import './container-details-panel.css';

/**
 * Tipo de pestañas disponibles
 */
type TabId = 'general' | 'status' | 'sensor' | 'maintenance' | 'location';

/**
 * Props del componente
 */
export interface ContainerDetailsPanelProps {
  /**
   * Datos del contenedor a mostrar/editar
   */
  container: WasteContainer;

  /**
   * Callback cuando se guarda el contenedor
   */
  onSave?: (updatedContainer: WasteContainer) => void;

  /**
   * Callback cuando se cancela la edición
   */
  onCancel?: () => void;

  /**
   * Callback cuando se elimina el contenedor
   */
  onDelete?: (containerId: string) => void;

  /**
   * Si true, el panel es de solo lectura: campos deshabilitados,
   * sin botones de guardado ni eliminación.
   * Se usa en modos distintos al modo Edición.
   */
  readOnly?: boolean;
}

/**
 * Componente ContainerDetailsPanel
 */
export function ContainerDetailsPanel({
  container,
  onSave,
  onCancel,
  onDelete,
  readOnly = false,
}: ContainerDetailsPanelProps): JSX.Element {
  const { projectId } = useProject();
  const { registerUnsavedChangesCheck } = useRightPanel();
  const [activeTab, setActiveTab] = useState<TabId>('general');
  const [formData, setFormData] = useState<WasteContainer>(container);
  const [originalData, setOriginalData] = useState<WasteContainer>(container); // Estado separado para el original
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  /**
   * Detecta si es un contenedor nuevo (no persistido en BD)
   * Los contenedores nuevos tienen IDs temporales que empiezan con 'temp-'
   */
  const isNewContainer = useMemo(() => {
    return container.id.startsWith('temp-');
  }, [container.id]);

  /**
   * Extrae coordenadas del GeoJSON
   */
  const coordinates: [number, number] = useMemo(() => {
    return [
      formData.geometry.coordinates[0],
      formData.geometry.coordinates[1],
    ];
  }, [formData.geometry.coordinates]);

  const originalCoordinates: [number, number] = useMemo(() => {
    return [
      container.geometry.coordinates[0],
      container.geometry.coordinates[1],
    ];
  }, [container.geometry.coordinates]);

  // Hook de codificación automática de objetos
  const { recodeStatus, isCheckingRecode, generateCode, confirmRecode } = useObjectCode({
    objectType: 'CN',
    projectId,
    isNewObject: isNewContainer,
    currentCode: container.code,
    objectId: isNewContainer ? undefined : container.id,
    coordinates,
    originalCoordinates,
  });

  // Hook para detectar cambios sin guardar
  const { hasUnsavedChanges, confirmDiscard } = useUnsavedChanges(
    originalData, // Estado original (sincronizado con formData)
    formData, // Estado actual
    {
      confirmMessage: isNewContainer
        ? '¿Descartar contenedor sin guardar?\n\nEl contenedor desaparecerá del mapa y todos los datos se perderán.'
        : '¿Descartar cambios del contenedor?\n\nLos cambios realizados se perderán, pero el contenedor se mantendrá con sus datos originales.',
    }
  );

  /**
   * Registrar función de verificación de cambios sin guardar
   */
  useEffect(() => {
    registerUnsavedChangesCheck(confirmDiscard);

    return () => {
      registerUnsavedChangesCheck(null);
    };
  }, [confirmDiscard, registerUnsavedChangesCheck]);

  /**
   * Resetear form cuando cambia el contenedor
   *
   * @remarks
   * IMPORTANTE: Resetea tanto formData como originalData para evitar
   * falsos positivos de "cambios sin guardar" al cambiar de contenedor.
   */
  useEffect(() => {
    setFormData(container);
    setOriginalData(container); // También resetear el original
  }, [container]);

  /**
   * Actualiza los datos del formulario
   */
  const handleFieldChange = <K extends keyof WasteContainer>(
    field: K,
    value: WasteContainer[K]
  ): void => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * Actualiza campos de metadata
   */
  const handleMetadataChange = (
    field: keyof NonNullable<WasteContainer['metadata']>,
    value: string
  ): void => {
    setFormData((prev) => ({
      ...prev,
      metadata: { ...prev.metadata, [field]: value },
    }));
  };

  /**
   * Copia las coordenadas al portapapeles
   */
  const handleCopyCoordinates = async (): Promise<void> => {
    const coordinatesText = `${coordinates[0]}, ${coordinates[1]}`;
    try {
      await navigator.clipboard.writeText(coordinatesText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error al copiar coordenadas:', err);
    }
  };

  /**
   * Guarda los cambios, generando o actualizando el código si corresponde.
   */
  const handleSave = async (): Promise<void> => {
    if (!projectId) return;
    setIsSaving(true);
    setError(null);

    try {
      let code = formData.code;

      if (isNewContainer && projectId) {
        const generatedCode = await generateCode();
        if (generatedCode) code = generatedCode;
      } else if (!isNewContainer && recodeStatus?.willRecode && projectId) {
        const newCode = await confirmRecode();
        if (newCode) code = newCode;
      }

      const updateDto: UpdateContainerDto = {
        containerType: formData.containerType,
        capacity: formData.capacity,
        status: formData.status,
        metadata: formData.metadata,
      };

      const updated = await wasteApi.containers.update(container.id, updateDto);

      const savedContainer = { ...updated, code };

      // Dispatch event
      window.dispatchEvent(new CustomEvent('container-updated', { detail: savedContainer }));

      // Marcar como guardado actualizando el original
      setOriginalData(savedContainer);

      if (onSave) {
        onSave(savedContainer);
      }
    } catch (err) {
      console.error('[ContainerDetailsPanel] Error al guardar:', err);
      setError(getWasteApiErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Cancela la edición con verificación de cambios
   */
  const handleCancelOrDiscard = async (): Promise<void> => {
    if (hasUnsavedChanges && !await confirmDiscard()) {
      return; // Usuario canceló el descarte, mantener panel abierto
    }
    // Usuario aceptó descartar: cerrar panel
    setFormData(container); // Resetear cambios
    onCancel?.();
  };

  /**
   * Elimina el contenedor con confirmación
   */
  const handleDelete = async (): Promise<void> => {
    // Primero verificar cambios sin guardar
    if (hasUnsavedChanges) {
      const discardConfirmed = await confirmDiscard();
      if (!discardConfirmed) {
        return; // Usuario canceló
      }
    }

    // Confirmar eliminación con ConfirmDialog
    const confirmed = await ConfirmDialog.show({
      title: '¿Eliminar contenedor?',
      message: 'Esta acción no se puede deshacer.\n\nEl contenedor será eliminado permanentemente de la base de datos.',
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      type: 'danger',
    });

    if (confirmed) {
      setIsSaving(true);
      try {
        await wasteApi.containers.delete(container.id);
        window.dispatchEvent(new CustomEvent('container-deleted', { detail: { containerId: container.id } }));
        onDelete?.(container.id);
      } catch (err) {
        setError(getWasteApiErrorMessage(err));
      } finally {
        setIsSaving(false);
      }
    }
  };

  return (
    <div className="container-details-panel">
      {/* Encabezado con código del objeto */}
      <div className="container-code-header">
        <span className="container-code-header__label">Código</span>
        <ObjectCodeBadge
          code={formData.code}
          isChecking={isCheckingRecode}
          isPending={isNewContainer}
        />
      </div>

      {/* Pestañas */}
      <div className="container-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={activeTab === 'general'}
          className={`container-tab ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          General
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'status'}
          className={`container-tab ${activeTab === 'status' ? 'active' : ''}`}
          onClick={() => setActiveTab('status')}
        >
          Estado
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'sensor'}
          className={`container-tab ${activeTab === 'sensor' ? 'active' : ''}`}
          onClick={() => setActiveTab('sensor')}
        >
          Sensor
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'maintenance'}
          className={`container-tab ${activeTab === 'maintenance' ? 'active' : ''}`}
          onClick={() => setActiveTab('maintenance')}
        >
          Mant.
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'location'}
          className={`container-tab ${activeTab === 'location' ? 'active' : ''}`}
          onClick={() => setActiveTab('location')}
        >
          Ubic.
        </button>
      </div>

      {/* Contenido de la pestaña activa */}
      <div className="container-tab-content">
        {/* Wrapper que deshabilita interacción en modo solo-lectura */}
        <div style={readOnly ? { pointerEvents: 'none', opacity: 0.65 } : undefined}>
          {/* Tab: General */}
          {activeTab === 'general' && (
            <div className="container-tab-panel">
              <div className="container-form-section">
                <h3 className="container-form-section-title">Identificación</h3>

                <div className="container-form-group">
                  <FormLabel tooltip="Código único de identificación del contenedor. Se genera automáticamente basado en la celda H3.">
                    Código único
                  </FormLabel>
                  <input
                    type="text"
                    className="container-form-input"
                    value={formData.code || ''}
                    disabled
                    placeholder="Código auto-generado"
                  />
                </div>

                <div className="container-form-group">
                  <FormLabel tooltip="Categoría del residuo que acepta este contenedor.">
                    Tipo de contenedor
                  </FormLabel>
                  <select
                    className="container-form-select"
                    value={formData.containerType}
                    onChange={(e) => handleFieldChange('containerType', e.target.value as any)}
                  >
                    {CONTAINER_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div className="container-form-group">
                  <FormLabel tooltip="Volumen máximo que puede almacenar el contenedor en litros.">
                    Capacidad (litros)
                  </FormLabel>
                  <input
                    type="number"
                    className="container-form-input"
                    value={formData.capacity}
                    onChange={(e) => handleFieldChange('capacity', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Tab: Estado */}
          {activeTab === 'status' && (
            <div className="container-tab-panel">
              <div className="container-form-section">
                <h3 className="container-form-section-title">Estado actual</h3>

                <div className="container-form-group">
                  <FormLabel tooltip="Estado operativo del contenedor.">
                    Estado
                  </FormLabel>
                  <select
                    className="container-form-select"
                    value={formData.status}
                    onChange={(e) => handleFieldChange('status', e.target.value as any)}
                  >
                    {CONTAINER_STATUSES.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>

                <div className="container-form-group">
                  <FormLabel tooltip="Porcentaje de llenado actual del contenedor. Actualizado por sensores IoT o manualmente.">
                    Nivel de llenado
                  </FormLabel>
                  <div className="container-fill-indicator">
                    <div
                      className="container-fill-bar"
                      style={{ width: `${formData.currentFill ?? 0}%` }}
                    />
                    <span className="container-fill-value">{formData.currentFill ?? 0}%</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tab: Sensor */}
          {activeTab === 'sensor' && (
            <div className="container-tab-panel">
              <div className="container-form-section">
                <h3 className="container-form-section-title">Sensores IoT</h3>

                <div className="container-form-group">
                  <FormLabel tooltip="Identificador único del sensor IoT instalado en el contenedor para monitoreo remoto del nivel de llenado.">
                    ID Sensor IoT
                  </FormLabel>
                  <input
                    type="text"
                    className="container-form-input"
                    value={formData.metadata?.sensorId ?? ''}
                    onChange={(e) => handleMetadataChange('sensorId', e.target.value)}
                    placeholder="Sin sensor asignado"
                  />
                </div>

                <div className="container-form-group">
                  <FormLabel tooltip="Umbral de llenado a partir del cual se genera una alerta para programar recolección.">
                    Umbral de alerta (%)
                  </FormLabel>
                  <input
                    type="number"
                    className="container-form-input"
                    value={formData.metadata?.alertThreshold ?? 80}
                    onChange={(e) => handleMetadataChange('alertThreshold', e.target.value)}
                    min="0"
                    max="100"
                    placeholder="80"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Tab: Mantenimiento */}
          {activeTab === 'maintenance' && (
            <div className="container-tab-panel">
              <div className="container-form-section">
                <h3 className="container-form-section-title">Historial de servicio</h3>

                <div className="container-form-group">
                  <FormLabel tooltip="Fecha y hora del último vaciado registrado.">
                    Último vaciado
                  </FormLabel>
                  <input
                    type="text"
                    className="container-form-input"
                    value={formData.metadata?.lastEmptied ?? 'No registrado'}
                    disabled
                  />
                </div>

                <div className="container-form-group">
                  <FormLabel tooltip="Fecha de instalación del contenedor en esta ubicación.">
                    Fecha de instalación
                  </FormLabel>
                  <input
                    type="date"
                    className="container-form-input"
                    value={formData.metadata?.installationDate ?? ''}
                    onChange={(e) => handleMetadataChange('installationDate', e.target.value)}
                  />
                </div>

                <div className="container-form-group">
                  <FormLabel tooltip="Notas adicionales sobre el mantenimiento del contenedor.">
                    Notas de mantenimiento
                  </FormLabel>
                  <textarea
                    className="container-form-textarea"
                    value={formData.metadata?.maintenanceNotes ?? ''}
                    onChange={(e) => handleMetadataChange('maintenanceNotes', e.target.value)}
                    placeholder="Observaciones, reparaciones, etc."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Tab: Ubicación */}
          {activeTab === 'location' && (
            <div className="container-tab-panel">
              <div className="container-form-section">
                <h3 className="container-form-section-title">Ubicación</h3>

                <div className="container-form-row container-coordinates-row">
                  <div className="container-form-group">
                    <FormLabel tooltip="Coordenada de longitud (Este-Oeste) en grados decimales.">
                      Longitud
                    </FormLabel>
                    <input
                      type="number"
                      className="container-form-input"
                      value={coordinates[0]}
                      disabled
                      step="0.000001"
                    />
                  </div>

                  <div className="container-form-group">
                    <FormLabel tooltip="Coordenada de latitud (Norte-Sur) en grados decimales.">
                      Latitud
                    </FormLabel>
                    <input
                      type="number"
                      className="container-form-input"
                      value={coordinates[1]}
                      disabled
                      step="0.000001"
                    />
                  </div>

                  <button
                    type="button"
                    className="container-copy-coords-btn"
                    onClick={handleCopyCoordinates}
                    title={copied ? 'Copiado!' : 'Copiar coordenadas'}
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>

                <div className="container-form-group">
                  <FormLabel tooltip="Dirección o referencia de ubicación del contenedor.">
                    Dirección
                  </FormLabel>
                  <input
                    type="text"
                    className="container-form-input"
                    value={formData.metadata?.address ?? ''}
                    onChange={(e) => handleMetadataChange('address', e.target.value)}
                    placeholder="Ej: Av. Principal 1234"
                  />
                </div>

                <div className="container-form-group">
                  <FormLabel tooltip="Zona de recolección asignada al contenedor.">
                    Zona
                  </FormLabel>
                  <input
                    type="text"
                    className="container-form-input"
                    value={formData.zone?.name ?? 'Sin zona asignada'}
                    disabled
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {projectId && !isNewContainer && (
        <PointAssociationSection
          projectId={projectId}
          entityType="waste_container"
          entityId={container.id}
          coordinates={coordinates}
        />
      )}

      {/* Aviso de cambio de código (solo cuando el contenedor se movió a otra celda) */}
      {recodeStatus?.willRecode && (
        <CodeChangeWarning
          currentCode={recodeStatus.currentCode}
          tentativeNewCode={recodeStatus.tentativeNewCode}
        />
      )}

      {/* Error */}
      {error && (
        <div className="container-error">
          <AlertTriangle size={14} />
          {error}
        </div>
      )}

      {/* Botones de acción */}
      <div className="container-actions">
        {readOnly ? (
          /* Solo lectura: único botón para cerrar */
          <button
            className="container-btn container-btn-secondary"
            onClick={onCancel}
          >
            Cerrar
          </button>
        ) : (
          /* Modo edición: guardar, cancelar, eliminar */
          <>
            <button
              className="container-btn container-btn-primary"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 size={14} className="container-spinner" />
                  Guardando...
                </>
              ) : (
                'Guardar'
              )}
            </button>

            <button
              className={isNewContainer ? 'container-btn container-btn-danger' : 'container-btn container-btn-secondary'}
              onClick={handleCancelOrDiscard}
              disabled={isSaving}
            >
              {isNewContainer ? 'Descartar' : 'Cancelar'}
            </button>

            {!isNewContainer && (
              <button
                className="container-btn container-btn-danger"
                onClick={handleDelete}
                disabled={isSaving}
              >
                Eliminar
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
